import { useState } from "react";
import { ArrowRight, ArrowLeft, CheckCircle2, RotateCcw, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  {
    key: "industry",
    title: "Which sector best describes your site?",
    options: [
      { value: "construction", label: "Construction" },
      { value: "substation", label: "Substation / Utilities" },
      { value: "industrial", label: "Industrial / Warehousing" },
      { value: "transport", label: "Transport / Logistics" },
      { value: "events", label: "Events" },
    ],
  },
  {
    key: "site_type",
    title: "What kind of footprint are we protecting?",
    options: [
      { value: "single", label: "Single building / Compound" },
      { value: "perimeter", label: "Large perimeter site" },
      { value: "multi", label: "Multi-site portfolio" },
      { value: "temporary", label: "Temporary / Pop-up" },
    ],
  },
  {
    key: "primary_concern",
    title: "What is your primary security concern?",
    options: [
      { value: "trespass", label: "Trespass & vandalism" },
      { value: "theft", label: "Asset / plant theft" },
      { value: "monitoring", label: "Continuous monitoring" },
      { value: "compliance", label: "Compliance & assurance" },
    ],
  },
  {
    key: "coverage",
    title: "What level of coverage do you need?",
    options: [
      { value: "out-of-hours", label: "Out-of-hours only" },
      { value: "24-7", label: "24/7 continuous" },
      { value: "remote", label: "Remote monitoring + response" },
      { value: "event", label: "Event-based" },
    ],
  },
];

export const SecurityQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "" });
  const [phase, setPhase] = useState("questions"); // questions | contact | result
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const current = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  const select = (val) => {
    const newAnswers = { ...answers, [current.key]: val };
    setAnswers(newAnswers);
    if (step < STEPS.length - 1) {
      setTimeout(() => setStep(step + 1), 180);
    } else {
      setTimeout(() => setPhase("contact"), 180);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz", {
        ...answers,
        contact_name: contact.name,
        contact_email: contact.email,
      });
      setResult(data);
      setPhase("result");
      toast.success("Recommendation ready", { description: "We've matched a tailored solution to your needs." });
    } catch (err) {
      toast.error("Couldn't submit", { description: "Please try again or call us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({}); setContact({ name: "", email: "" }); setStep(0); setResult(null); setPhase("questions");
  };

  return (
    <section data-testid="quiz-section" id="quiz" className="relative py-24 lg:py-32 border-y border-white/10 bg-ink-900/40">
      <div className="absolute inset-0 grid-bg-fine opacity-50 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">
              ◆ Interactive · 60 Seconds
            </p>
            <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl leading-[1.05] tracking-tighter mb-5">
              Build your security <span className="text-gradient-cyan">blueprint</span>.
            </h2>
            <p className="text-slate-400 leading-relaxed mb-7">
              Answer four quick questions and we'll match you to the right combination of officers, technology and monitoring — instantly.
            </p>
            <div className="space-y-3">
              {["Tailored recommendation", "No spam, ever", "Reviewed by a UK consultant"].map((b) => (
                <div key={b} className="flex items-center gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8">
            <div data-testid="quiz-card" className="relative glass rounded-2xl p-7 lg:p-10">
              {/* Progress */}
              {phase !== "result" && (
                <div className="flex items-center justify-between mb-7">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {phase === "questions" ? `Step ${step + 1} of ${STEPS.length}` : "Final · Contact"}
                  </div>
                  <div className="flex-1 mx-5 h-px bg-white/10 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-cyan-400 transition-all duration-300" style={{ width: phase === "contact" ? "100%" : `${progress}%` }} />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">
                    {phase === "contact" ? "100" : Math.round(progress)}%
                  </div>
                </div>
              )}

              {phase === "questions" && (
                <div>
                  <h3 data-testid="quiz-question" className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight mb-6">
                    {current.title}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {current.options.map((opt) => (
                      <button
                        key={opt.value}
                        data-testid={`quiz-option-${opt.value}`}
                        onClick={() => select(opt.value)}
                        className={`group text-left px-5 py-4 rounded-lg border transition ${
                          answers[current.key] === opt.value
                            ? "border-cyan-400/60 bg-cyan-500/10"
                            : "border-white/10 bg-white/5 hover:border-cyan-400/30 hover:bg-white/10"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-white">{opt.label}</span>
                          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition" />
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <button
                      data-testid="quiz-back"
                      onClick={() => step > 0 && setStep(step - 1)}
                      disabled={step === 0}
                      className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                  </div>
                </div>
              )}

              {phase === "contact" && (
                <form onSubmit={submit} className="space-y-5">
                  <h3 className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight">
                    Almost there. Where do we send the blueprint?
                  </h3>
                  <p className="text-sm text-slate-400">No spam. A consultant reviews each result personally.</p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      data-testid="quiz-name"
                      required
                      type="text"
                      placeholder="Full name"
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      className="w-full bg-ink-900 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50 transition"
                    />
                    <input
                      data-testid="quiz-email"
                      required
                      type="email"
                      placeholder="Work email"
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      className="w-full bg-ink-900 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50 transition"
                    />
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setPhase("questions")}
                      className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button
                      data-testid="quiz-submit"
                      type="submit"
                      disabled={submitting}
                      className="ml-auto inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-ink-950 font-bold rounded-md px-5 py-3 text-sm transition"
                    >
                      {submitting ? "Generating..." : "Get my blueprint"}
                      <Sparkles className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              )}

              {phase === "result" && result && (
                <div data-testid="quiz-result" className="text-center sm:text-left">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-300">Recommendation ready</span>
                  </div>
                  <h3 className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight mb-3">
                    Your tailored security blueprint
                  </h3>
                  <p className="text-slate-400 mb-6">Based on your responses, our consultants suggest the following solution stack. A team member will be in touch within one working day.</p>

                  <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    {result.recommendation.map((r, i) => (
                      <div key={r} className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-cyan-400/20">
                        <span className="font-mono text-cyan-400 text-xs">0{i + 1}</span>
                        <span className="text-white font-medium">{r}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-ink-950 font-bold rounded-md px-5 py-3 text-sm transition"
                    >
                      Speak to a consultant <ArrowRight className="w-4 h-4" />
                    </a>
                    <button
                      data-testid="quiz-reset"
                      onClick={reset}
                      className="inline-flex items-center gap-2 border border-white/20 hover:bg-white/5 text-white rounded-md px-5 py-3 text-sm transition"
                    >
                      <RotateCcw className="w-4 h-4" /> Run again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
